import BlockingForm from "collections/Form/BlockingForm";
import { DESKTOP_DEVICE_FORM_SETTINGS } from "Constants";
import { Loader, LoadingIndicator, PolyIcon } from "elements";
import {
  Capabilities,
  Capability,
  Device,
  HardwareModel,
  Maybe,
  PolicyType,
  useAvailablePolicyCapabilitiesQuery,
  UserGroup
} from "generated/graphql";
import _ from "lodash";
import { NavLink, Route, Switch, useHistory } from "polyrouter";
import { AllQueryParam } from "polyrouter/allQueryParams";
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { DetailProps } from "routes/Manage/Inventory/View/DeviceDetails";
import "routes/Manage/Inventory/View/DeviceDetails.scss";
import { State } from "store";
import { resetDesktopDeviceSettingInheritance } from "store/desktopDeviceSettingInheritance";
import {
  resetDesktopDeviceSettingChanges,
  resetDesktopDeviceSettingsInheritanceAndNav
} from "store/desktopDeviceSettings";
import { setNavItem, noNavItems } from "store/settingsNavItems";
import { createReducer, createStandardAction } from "typesafe-actions";
import { maskTypename } from "urql";
import i18n, { useTranslation } from "utilities/i18n";
import { DESKTOP_DEVICE_SETTINGS_NAV } from "../Settings-Shared/DeviceSettingsComponents";
import {
  GroupInfo,
  SettingChangeIndicator,
  SiteInfo
} from "../Settings/ProvisionedDeviceSettingsRouter";
import {
  useGetInitialValues,
  useGroupPolicyQuery,
  useDeviceGroupPolicyQuery
} from "./hooks";
import { getUnsavedChangesCount } from "./MyDesktopDeviceSettings";
import { ProvisionedDesktopDeviceSettings } from "./ProvisionedDesktopDeviceSettings";
import { BasicDesktopDeviceSettings } from "./schemas/types";
import { generateC10String, getC10Value } from "./utilities/c10";
import { generateBlankDesktopDeviceSettings } from "./utilities/utils";
import { Animation } from "elements";
import darkAnimation from "assets/animations/Lens-Empty-Dark.json";
import lightAnimation from "assets/animations/Lens-Empty-Light.json";
import { DesktopInheritanceLoader } from "routes/Manage/Policy/DesktopInheritanceLoader";

export type HardwareModelProps = Pick<
  HardwareModel,
  "id" | "name" | "supportsPolicies" | "aliases"
>;
export type UserGroupInfo = Pick<UserGroup, "id" | "name">;
export type DevicePolicyInfo = Pick<Device, "id" | "name" | "productId">;

export enum DesktopDevicePolicyScope {
  Model = "MODEL",
  Group = "GROUP",
  Site = "SITE",
  User_Group = "USER_GROUP",
  Device = "DEVICE"
}

export type DesktopDevicePolicyScopeState = {
  desktopDevicePolicyScope: DesktopDevicePolicyScope;
};

export type SetDesktopDevicePolicyScope = DesktopDevicePolicyScope;

const defaultDesktopDevicePolicyScopeState = {
  desktopDevicePolicyScope: DesktopDevicePolicyScope.Model
};

export const setDesktopDevicePolicyScope = createStandardAction(
  "SET_DESKTOP_DEVICE_POLICY_SCOPE"
)<SetDesktopDevicePolicyScope>();

export const DesktopDevicePolicyScopeReducer = createReducer(
  defaultDesktopDevicePolicyScopeState
).handleAction(setDesktopDevicePolicyScope, (state, action) => {
  return {
    ...state,
    desktopDevicePolicyScope: action.payload
  };
});

export const DesktopDevicePolicyScopeContext = createContext<
  [DesktopDevicePolicyScopeState, React.Dispatch<any>]
>([defaultDesktopDevicePolicyScopeState, () => null]);

export const useDesktopDevicePolicyScopeContext = () => {
  return useContext(DesktopDevicePolicyScopeContext);
};

export const DesktopDevicePolicyScopeProvider: React.FC = props => {
  const [state, dispatch] = useReducer(
    DesktopDevicePolicyScopeReducer,
    defaultDesktopDevicePolicyScopeState
  );
  return (
    <DesktopDevicePolicyScopeContext.Provider
      value={[state, dispatch]}
      {...props}
    />
  );
};

export type CapabilitiesMap = { [key: string]: any } | undefined;

export const constructGroupPolicyName = (
  model: string,
  type: PolicyType,
  scope: DesktopDevicePolicyScope,
  siteName?: string,
  groupName?: string,
  userGroupName?: string,
  deviceId?: string
) => {
  let policyType = "Update";
  if (type === PolicyType.DeviceSettingsUpdate) {
    policyType = "Settings";
  }

  let policyScope: string = "Unknown";
  let identifier: string | undefined = "Unknown";
  if (scope === DesktopDevicePolicyScope.Site) {
    policyScope = "Site";
    identifier = siteName;
  }
  if (scope === DesktopDevicePolicyScope.Group) {
    policyScope = "Device Group";
    identifier = groupName;
  }
  if (scope === DesktopDevicePolicyScope.User_Group) {
    policyScope = "Device User Group";
    identifier = userGroupName;
  }
  if (scope === DesktopDevicePolicyScope.Device) {
    policyScope = "Device";
    identifier = deviceId;
  }

  if (scope === DesktopDevicePolicyScope.Model) {
    policyScope = "Model";
    return i18n.t("POLICY_NAME_MODEL", { model, policyScope, policyType });
  }

  return i18n.t("POLICY_NAME", { model, policyScope, identifier, policyType });
};

export const ProvisionedDesktopSettingsNav: React.FC = () => {
  const history = useHistory();
  const { t } = useTranslation();
  const [filter, setFilter] = useState("");
  const unsavedSettings = useSelector(
    (state: State) => state.desktopDeviceSettings
  );

  const isCategoryChanged = (category: string) => !!unsavedSettings[category];

  const changedSettingsCount = getUnsavedChangesCount(unsavedSettings);
  const persistParams: AllQueryParam[] = [
    "PolicyFilterState",
    "deviceModel",
    "device",
    "site",
    "user_group",
    "DeviceFilterState",
    "InsightsFilterState",
    "group",
    "previous"
  ];

  const { navItems, loading } = useSelector((state: State) => {
    return state.settingsNavItems;
  });

  const filterVal = (val: string) =>
    val.toLowerCase().includes((filter ?? "").toLowerCase());
  return (
    <Loader loading={loading} className="inline text-left">
      <ul className="menu router sub scroll">
        <BlockingForm
          unsavedChanges={Object.keys(unsavedSettings).length > 0}
          allowedSubRoutes={[
            "/settings",
            "/my-settings",
            "/manage/policy/details"
          ]}
        >
          <>
            <li className="filter mb-1 mt-1">
              <PolyIcon icon="filter" className="v-middle" />
              <input
                type={"text"}
                id={"filter-input"}
                className="form-control floating-input"
                placeholder={t("FILTER_SETTINGS")}
                value={filter ?? ""}
                onChange={e => setFilter(e.target.value)}
              />
            </li>
            <li>
              <NavLink
                nest
                to={{
                  pathname: "/my-settings",
                  search: history.location.search
                }}
              >
                {changedSettingsCount > 0 && <SettingChangeIndicator />}
                {t("MY_SETTINGS")}
              </NavLink>
            </li>
            <li className="divider mt-5" />
            {navItems
              .filter(({ title }) => filterVal(title))
              .map(({ title, url }) => (
                <li key={title + "key"}>
                  <NavLink persist={persistParams} nest exact to={url}>
                    {isCategoryChanged(title) && <SettingChangeIndicator />}
                    {title}
                  </NavLink>
                </li>
              ))}
          </>
        </BlockingForm>
      </ul>
    </Loader>
  );
};

export const getInitialFormValues = (
  policyCapabilities: Pick<Capability, "id" | "constraints">[] | undefined
) => {
  const extendedPolicyCapabilities: {
    id: string;
    type: string;
    options?: Maybe<string[]>;
  }[] = [];

  Object.values(DESKTOP_DEVICE_FORM_SETTINGS).forEach(settings => {
    settings.forEach(setting => {
      policyCapabilities?.forEach(capability => {
        if (capability.id.includes(setting.accessor)) {
          extendedPolicyCapabilities.push({
            id: capability.id,
            type: setting.type,
            options: capability.constraints?.options
          });
        }
      });
    });
  });

  const initialValues = generateBlankDesktopDeviceSettings();
  const getInitialValues = (
    path: string[],
    obj: Record<any, any>,
    type: string,
    options?: Maybe<string[]>
  ) => {
    let key = path[0];
    if (path.length === 1) {
      switch (type) {
        case "checkbox":
          obj[key] = {
            value: false,
            constraints: {
              options: options
            }
          };
          break;
        case "dropdown":
          obj[key] = {
            value: "",
            constraints: {
              options: options
            }
          };
          break;
        case "slider":
          if (options) {
            const [min, max, step] = options;
            if (min === "0" && max === "1" && step === "1") {
              obj[key] = {
                value: false,
                constraints: {
                  options: options
                }
              };
              break;
            }
          }

          obj[key] = {
            // Max value for slider lives at index 1 of options
            // so we set the default value to half of that
            value: options ? Math.floor(parseInt(options[1]) / 2) : "1",
            constraints: {
              options: options
            }
          };
          break;
      }
    } else {
      if (!obj[key]) {
        obj[key] = {};
      }
      getInitialValues(path.slice(1), obj[key], type, options);
    }
  };

  (extendedPolicyCapabilities || []).forEach(capability => {
    getInitialValues(
      capability.id.split("."),
      initialValues,
      capability.type,
      capability.options
    );
  });

  return initialValues;
};

export const getCapabilityMap = (
  obj: BasicDesktopDeviceSettings | Capabilities
): CapabilitiesMap => {
  if (Object.keys(obj).length === 0) {
    return;
  }
  const capabilities: { [key: string]: any } = {};
  const traverseC10Obj = (obj: any, nodes: Array<string>) => {
    Object.keys(obj).forEach(key => {
      if (typeof obj[key] === "object" && obj[key] !== null) {
        traverseC10Obj(obj[key], [...nodes, key]);
      }
      if (
        ["boolean", "string", "number"].includes(typeof obj[key]) &&
        key !== "__typename"
      ) {
        // create reverse domain name from nodes collected along traversal
        const id = [...nodes, key].join(".");
        // get value for setting
        const value: string = obj[key];
        capabilities[id] = value;
      }
    });
  };

  traverseC10Obj(obj, []);
  return capabilities;
};

export const ProvisionedDesktopDeviceSettingsRouter: React.FC<{
  device?: DetailProps;
  hardwareModel?: HardwareModelProps;
  site?: SiteInfo;
  group?: GroupInfo;
  userGroup?: UserGroupInfo;
  devicePolicyInfo?: DevicePolicyInfo;
}> = ({ device, hardwareModel, site, group, userGroup, devicePolicyInfo }) => {
  const dispatch = useDispatch();
  const policySettingCache = useRef<BasicDesktopDeviceSettings | null>();
  const { t } = useTranslation();
  useEffect(() => {
    return () => {
      dispatch(resetDesktopDeviceSettingsInheritanceAndNav());
    };
  }, [dispatch]);

  const {
    data: policyCapabilities,
    fetching: fetchingPolicyCapabilities,
    executor
  } = useAvailablePolicyCapabilitiesQuery({
    variables: {
      propertyValue: hardwareModel?.id || "",
      propertyPath: "com.poly.product.model.catalog_id"
    }
  });

  // c10List.capabilities will always include 'com.poly.network_information.interfaces'
  // so we need to make sure the array is < 2 since it will always have 1 element
  const isC10ListEmpty =
    policyCapabilities?.capabilitiesSuperset &&
    policyCapabilities.capabilitiesSuperset.length < 2;

  const initalFormValues: BasicDesktopDeviceSettings = getInitialFormValues(
    policyCapabilities?.capabilitiesSuperset
  );
  let policyScope: DesktopDevicePolicyScope;
  if (devicePolicyInfo?.id) {
    policyScope = DesktopDevicePolicyScope.Device;
  } else if (userGroup?.id) {
    policyScope = DesktopDevicePolicyScope.User_Group;
  } else if (site?.id) {
    policyScope = DesktopDevicePolicyScope.Site;
  } else if (group?.id) {
    policyScope = DesktopDevicePolicyScope.Group;
  } else {
    policyScope = DesktopDevicePolicyScope.Model;
  }

  const { devicePolicy, fetchingDevicePolicy } = useDeviceGroupPolicyQuery(
    isC10ListEmpty,
    policyCapabilities?.capabilitiesSuperset,
    devicePolicyInfo,
    fetchingPolicyCapabilities
  );

  const {
    groupPolicy,
    fetchingGroupPolicy,
    noSettingsToDisplay
  } = useGroupPolicyQuery(
    isC10ListEmpty,
    policyCapabilities,
    group,
    site,
    userGroup,
    hardwareModel,
    fetchingPolicyCapabilities
  );

  let settingsPolicy = groupPolicy?.policyCapabilities.sources[0];
  let currentScopeCapabilities = groupPolicy?.policyCapabilities.capabilities;
  if (policyScope === DesktopDevicePolicyScope.Device) {
    settingsPolicy = devicePolicy?.devicePolicyCapabilities.sources.find(
      policy => policy.type === "device"
    );

    currentScopeCapabilities =
      devicePolicy?.devicePolicyCapabilities.capabilities;
  }

  useEffect(() => {
    if (noSettingsToDisplay) dispatch(noNavItems());
  }, [noSettingsToDisplay, dispatch]);

  const cleanedCurrentScopeCapabilities = useGetInitialValues(
    maskTypename(currentScopeCapabilities)
  );

  let cleanedCapabilities = cleanedCurrentScopeCapabilities;

  if (!policySettingCache.current && cleanedCapabilities) {
    policySettingCache.current = cleanedCapabilities;
  }

  if (
    policySettingCache.current &&
    !_.isEqual(policySettingCache.current, cleanedCapabilities)
  ) {
    dispatch(resetDesktopDeviceSettingChanges());
    dispatch(resetDesktopDeviceSettingInheritance());
    policySettingCache.current = null;
  }

  let capabilitiesMap: CapabilitiesMap;
  if (cleanedCapabilities) {
    capabilitiesMap = getCapabilityMap(cleanedCapabilities);
    if (capabilitiesMap) {
      Object.entries(capabilitiesMap).forEach(([propertyPath, value]) => {
        _.set(initalFormValues, propertyPath, value);
      });
    }
  }

  const categoryWithSettings = useMemo<Array<string>>(() => {
    const categories: string[] = [];
    if (initalFormValues) {
      Object.entries(DESKTOP_DEVICE_FORM_SETTINGS).forEach(
        ([category, categorySettings]) => {
          const settingsWithValues = categorySettings.filter(setting => {
            const values = getC10Value(
              initalFormValues,
              generateC10String(setting.accessor, false).split(".")
            );
            return values !== undefined && values.value !== undefined;
          });
          if (settingsWithValues.length > 0) {
            categories.push(category);
          }
        }
      );
    }
    return categories;
  }, [initalFormValues]);

  useEffect(() => {
    categoryWithSettings.forEach(category => {
      dispatch(setNavItem(DESKTOP_DEVICE_SETTINGS_NAV[category]));
    });
  }, [categoryWithSettings, dispatch]);

  if (
    fetchingPolicyCapabilities ||
    fetchingGroupPolicy ||
    fetchingDevicePolicy
  ) {
    return <LoadingIndicator />;
  }

  return (
    <>
      {!groupPolicy && noSettingsToDisplay ? (
        <div data-testid="no-results" className="no-results-indicator">
          <Animation
            animation={{
              light: lightAnimation,
              dark: darkAnimation
            }}
            title={t("NO_DESKTOP_SETTINGS_AVAILABLE")}
            titleStyle="desktop-device-settings"
          />
        </div>
      ) : (
        <DesktopDevicePolicyScopeProvider>
          <Switch>
            <Route nest path="/">
              <ProvisionedDesktopDeviceSettings
                device={device}
                isPolicyView={!device}
                hardwareModel={hardwareModel}
                site={site}
                group={group}
                userGroup={userGroup}
                policyScope={policyScope}
                settingsPolicy={settingsPolicy}
                deviceSettings={initalFormValues}
                categoryWithSettings={categoryWithSettings}
                capabilitiesMap={capabilitiesMap}
                refreshData={executor}
                devicePolicyInfo={devicePolicyInfo}
              />
              {devicePolicyInfo?.id && (
                <DesktopInheritanceLoader
                  device={devicePolicyInfo}
                  settings={devicePolicy?.devicePolicyCapabilities.capabilities}
                  sources={devicePolicy?.devicePolicyCapabilities.sources}
                  hardwareModel={hardwareModel}
                />
              )}
            </Route>
          </Switch>
        </DesktopDevicePolicyScopeProvider>
      )}
    </>
  );
};
